import React, { useState } from "react";
import { Container, Row, Alert } from "reactstrap";
import * as Yup from "yup";
import Router from "next/router";
import { useField } from "formik";
import { ulid } from "ulid";

import useApi from "~/hooks/use-api";
import Form from "~/components/Form";
import TextField from "~/components/TextField";
import CurrencyField from "~/components/CurrencyField";
import TimezoneField from "~/components/TimezoneField";
import Link from "next/link";

interface OrganizationPayload {
  uid: string;
  name: string;
  timezone: string;
  currency: string;
  slug: string;
  websiteUrl: string;
  merchantEmail: string;
}

const OrganizationPayloadSchema = Yup.object<OrganizationPayload>({
  uid: Yup.string(),
  name: Yup.string().required("Name is a required field"),
  timezone: Yup.string().required("Timezone is a required field"),
  currency: Yup.string().required("Currency is a required field"),
  slug: Yup.string()
    .matches(/^[a-z]/i, "First character must be letter")
    .matches(/[a-z0-9]$/i, "Last character must be a letter or number")
    // This is the full regex pattern, patterns above are for better messaging
    .matches(
      /^[a-z](?:[a-z0-9-]*[a-z0-9])?$/,
      "Only lower case letters, numbers and dashes are allowed"
    )
    .required(),
  websiteUrl: Yup.string(),
  merchantEmail: Yup.string().required("Merchant's email is a required field"),
});

const initialValues: OrganizationPayload = {
  uid: "",
  name: "",
  timezone: "",
  currency: "",
  slug: "",
  websiteUrl: "",
  merchantEmail: "",
};
interface Organization {
  uid: string;
  name: string;
  timezone: string;
  currency: string;
  disabled: string;
  stripeAccountId: string;
}

export default function NewOrgPage() {
  // useState is being used more like useMemo here,
  // however useState has stricter semantics
  const [newOrgUid] = useState(ulid());
  const { data: orgs, revalidate } = useApi<Organization[]>(
    "/api/organization?includeDisabled=true"
  );

  return (
    <Container style={{ maxWidth: 700 }}>
      <h4 className="my-3">Create a new organisation</h4>
      <Form
        new
        path="/api/organization"
        validationSchema={OrganizationPayloadSchema}
        initValues={{ ...initialValues, uid: newOrgUid }}
        onSave={(org?: { uid: string }) => {
          if (!org) return;
          Router.push("/orgs/[org]", `/orgs/${org.uid}`);
        }}
        onError={() => revalidate()}
      >
        <DuplicateOrgNameAlert orgs={orgs} />

        <TextField label="Name" name="name" />

        <Row>
          <TextField
            col={"6"}
            label="Slug"
            name="slug"
            help="A slug is a human friendly ID for the organization, it will be used for app urls"
          />
          <TextField
            col={"6"}
            label="Website"
            name="websiteUrl"
            placeholder="https://example.com"
          />
          <TextField
            col={"6"}
            label="Merchant User Email"
            help="The initial user email for Tidy Office"
            name="merchantEmail"
            type="email"
            placeholder="admin@example.com"
          />
        </Row>

        <Row>
          <TimezoneField col={"6"} name="timezone" />
          <CurrencyField col={"6"} name="currency" />
        </Row>

        <Alert color="info" className="mb-3">
          <p>
            The default bundle ID for this app will be{" "}
            <GeneratedFieldPreview from="websiteUrl" mapValue={mapBundleId} />
          </p>
          <p>
            The default ordering url for this app will be{" "}
            <GeneratedFieldPreview
              from="slug"
              mapValue={(v) => `https://${v || "slug"}.loke.app`}
            />
          </p>
          {/* If only we had a Stack component, mb-0 for now */}
          <p className="mb-0">
            App urls will look like{" "}
            <GeneratedFieldPreview
              from="slug"
              mapValue={(v) => `${v || "slug"}://legals`}
            />
          </p>
        </Alert>
      </Form>
    </Container>
  );
}

function mapBundleId(url: string) {
  url = url.startsWith("http") ? url : "https://" + url;

  try {
    return new URL(url).hostname.split(".").reverse().join(".");
  } catch {
    return "com.example";
  }
}

function DuplicateOrgNameAlert(props: { orgs: Organization[] | null }) {
  const { orgs } = props;
  const [field] = useField<string>("name");

  const normalizedField = field.value.toLowerCase().trim();
  const org =
    orgs && orgs.find((o) => o.name.toLowerCase().trim() === normalizedField);

  if (!org) {
    return null;
  }

  return (
    <Alert color="warning">
      It looks like an organisation called{" "}
      <Link href={`/orgs/${org.uid}`}>
        <a>{org.name}</a>
      </Link>{" "}
      exists, you should probably just edit it.
    </Alert>
  );
}

function GeneratedFieldPreview(props: {
  from: string;
  mapValue(value: string): string;
}) {
  const [field, meta] = useField<string>(props.from);

  return (
    <strong>
      {props.mapValue(meta.touched ? field.value : meta.initialValue || "")}
    </strong>
  );
}
